import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useMutation, useQuery } from '@apollo/client';
import to from 'await-to-js';
import { Undo2 } from 'lucide-react';
import Path from 'path-to-regexp';
import { useHistory, useParams } from 'react-router';

import { Button } from '@/shadcn/button';

import {
  EditMembershipActionType,
  type GroupPatch,
  RoleType
} from '@/apollo/graphql';
import { ErrorContext } from '@/components/errorBoundary/errorBoundary';
import { Navbar } from '@/components/layouts';
import { notify } from '@/components/toast';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import {
  ROUTE_USER_DASHBOARD,
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import {
  ADD_USERS_MUTATION,
  GROUP_SETTINGS_QUERY,
  UPDATE_GROUP_MEMBERS_MUTATION,
  UPDATE_GROUP_MUTATION
} from '@/gql/queries/groups/groupSettings.query.gql';
import { TestId } from '@/types/testId';
import { pluralize } from '@/utils';
import { AddParticipantsModal } from './addParticipantsModal/addParticipantsModal';
import { ParticipantsList } from './participantsList/participantsList';
import { RemoveGroupMembersModal } from './removeGroupMembersModal/removeGroupMembersModal';
import { SubHeader } from './subHeader/subHeader.groupSettings';
import { getGroupParticipants } from './utils';

export const GroupSettings = memo(() => {
  const { handleError } = useContext(ErrorContext);
  const history = useHistory();
  const params = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(GROUP_SETTINGS_QUERY, {
    variables: {
      groupRowId: params.id
    },
    fetchPolicy: 'cache-and-network'
  });

  const [updateGroupMutation] = useMutation(UPDATE_GROUP_MUTATION);
  const [addUsersMutation] = useMutation(ADD_USERS_MUTATION);
  const [updateGroupMembersMutation] = useMutation(
    UPDATE_GROUP_MEMBERS_MUTATION
  );

  const group = data?.groupByRowId;
  // set doc title at mount
  useEffect(() => {
    if (!group?.name) return;
    document.title = `${group.name} - Group - Trace`;
  }, [group]);

  // Raise 404 page when group not found.
  const groupError = error || !(loading || group);

  useEffect(() => {
    // This check avoids rerendering the component after the erroBoundary has been triggered
    if (groupError) {
      handleError('group', params.id, ROUTE_USER_DASHBOARD);
    }
  }, [groupError, params, handleError]);

  // memoized header config
  const header = useMemo(() => {
    const headerConfig = {
      loading,
      bottomLevel: {
        workflowPage: false,
        infoContext: {
          links: [
            {
              icon: 'Group',
              label: 'Groups settings',
              path: group?.workflow?.rowId
                ? Path.compile(ROUTE_WORKFLOW_GROUPS)({
                    id: group.workflow.rowId
                  })
                : undefined
            },
            {
              label: group?.name
            }
          ]
        }
      }
    };
    if (group?.workflow) {
      headerConfig.bottomLevel.infoContext.links.unshift({
        icon: 'TableColumns',
        label: group.workflow?.name ?? 'Workflow',
        path: Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
          id: group.workflow?.rowId
        })
      });
    }

    return <Navbar config={headerConfig} />;
  }, [group]);

  // go back to workflow groups page
  const goBack = useCallback(() => {
    history.push(
      Path.compile(ROUTE_WORKFLOW_GROUPS)({
        id: group?.workflow?.rowId
      })
    );
  }, [history, group]);

  // update group mutation callback
  const updateGroup = useCallback(
    async (patch: GroupPatch) => {
      if (!group) return;
      const updateGroupPromise = updateGroupMutation({
        variables: {
          groupRowId: group.rowId,
          patch
        }
      });

      notify.promise(updateGroupPromise, {
        loading: '🛠️ Updating group name...',
        success: `Group ${patch.name || group.name} was correctly updated`,
        error: data => {
          console.error('Group update error: ', data);
          return 'Something went wrong during the group update...';
        }
      });
      await to(updateGroupPromise);
    },
    [group, updateGroupMutation]
  );

  // state to remove a set of members from the group
  const [membersToRemove, setMembersToRemove] = useState<{
    membersAccountIds: string[];
    participantsStr: string;
  } | null>(null);

  // callback passed to children to trigger the confirmation modal
  const triggerRemoveGroupMemberModal = useCallback(
    (membersAccountIds, participantsStr) => {
      setMembersToRemove({ membersAccountIds, participantsStr });
    },
    []
  );

  // callback to submit removal of group members
  const removeGroupMembers = useCallback(async () => {
    if (!(membersToRemove && group)) return;
    const { membersAccountIds, participantsStr } = membersToRemove;
    const removeParticipantPromise = updateGroupMembersMutation({
      variables: {
        groupId: group.rowId,
        members: membersAccountIds.map(memberId => ({
          memberId,
          action: EditMembershipActionType.Revoke,
          role: RoleType.Reader
        }))
      }
    });

    notify.promise(removeParticipantPromise, {
      loading: '🧹 Removing participants from the group...',
      success: `${participantsStr} correctly removed from Group ${group.name}`,
      error: data => {
        console.error('Group participants removal error: ', data);
        return 'Something went wrong during the group participants removal...';
      }
    });

    const [err] = await to(removeParticipantPromise);

    if (err) return;

    // clear the members to remove to close the modal
    setMembersToRemove(null);
  }, [membersToRemove, group, updateGroupMembersMutation]);

  const [showAddParticipantsModal, setShowAddParticipantsModal] =
    useState(false);

  // callback to submit addition of users from their email
  const addUsers = useCallback(
    async usersEmails => {
      if (!group) return;
      const addUserPromise = addUsersMutation({
        variables: {
          groupRowId: group.rowId,
          emails: usersEmails
        }
      });
      notify.promise(addUserPromise, {
        loading: '🧹 Adding users to the group...',
        success: `${pluralize(
          usersEmails.length,
          'user'
        )} correctly added to Group ${group.name}`,
        error: data => {
          console.error('Group users addition error: ', data);
          return 'Something went wrong during the group users addition...';
        }
      });
      const [err] = await to(addUserPromise);

      if (err) return;

      // if everything went well close the modal
      setShowAddParticipantsModal(false);
    },
    [group, addUsersMutation, setShowAddParticipantsModal]
  );

  // callback to submit addition of teams from their accountId
  const addTeams = useCallback(
    async (teamsAccountIds: number[]) => {
      if (!group) return;
      const addTeamPromise = updateGroupMembersMutation({
        variables: {
          groupId: group.rowId,
          members: teamsAccountIds.map(memberId => ({
            memberId: memberId.toString(),
            action: EditMembershipActionType.Grant,
            role: RoleType.Reader
          }))
        }
      });

      notify.promise(addTeamPromise, {
        loading: '🧹 Adding teams to the group...',
        success: `${pluralize(
          teamsAccountIds.length,
          'team'
        )} correctly added to Group ${group.name}`,
        error: data => {
          console.error('Group teams addition error: ', data);
          return 'Something went wrong during the group teams addition...';
        }
      });

      const [err] = await to(addTeamPromise);

      if (err) return;

      // if everything went well close the modal
      setShowAddParticipantsModal(false);
    },
    [group, updateGroupMembersMutation, setShowAddParticipantsModal]
  );

  // memoized group participants
  const {
    otherParticipants = [],
    teams = [],
    nbParticipants
  } = useMemo(
    () =>
      group
        ? getGroupParticipants(group.members.nodes)
        : {
            otherParticipants: [],
            teams: [],
            nbParticipants: 0
          },
    [group]
  );

  // memoized sections list
  const sectionsList = useMemo(() => {
    if (loading) return <TraceIconSpinner />;

    // if no participant at all display a specific message
    if (!(teams.length > 0 || otherParticipants.length > 0))
      return <div className="p-5">No participants to display here</div>;

    // otherwise display first the list of 'other participants'
    // and then all teams
    return (
      <div className="flex flex-col flex-nowrap items-stretch px-5 py-1">
        {otherParticipants.length > 0 && (
          <ParticipantsList
            participants={otherParticipants}
            removeGroupMembers={triggerRemoveGroupMemberModal}
            title="Other Participants"
          />
        )}
        {teams.map(team => (
          <ParticipantsList
            key={team.accountId}
            participants={team.participants}
            removeGroupMembers={triggerRemoveGroupMemberModal}
            teamAccountId={team.accountId}
            title={team.name}
          />
        ))}
      </div>
    );
  }, [loading, otherParticipants, teams, nbParticipants]);

  return (
    <>
      {/* <div id={TOOLTIP_PORTAL} /> */}
      {header}
      {/* Height and right edge subtract the workspace frame headroom (0 unless
          the screen border is up) so content and scrollbars end above the
          frame instead of under it. */}
      <div className="fixed top-full-navbar right-[var(--workspace-frame-headroom,0px)] left-0 flex h-[calc(100%-var(--spacing-full-navbar)-var(--workspace-frame-headroom,0px))] flex-col flex-nowrap items-stretch bg-background text-foreground">
        <SubHeader
          group={group}
          nbParticipants={nbParticipants}
          nbTeams={teams.length}
          setShowAddParticipantsModal={setShowAddParticipantsModal}
          updateGroup={updateGroup}
        />
        <div className="grow overflow-auto">{sectionsList}</div>
        <div className="flex h-[63px] shrink-0 flex-row flex-nowrap items-start justify-between px-5 py-2.5">
          <Button
            aria-label="Click to go back to the groups page"
            data-cy={TestId.GroupSettings.GoBackButton}
            disabled={false}
            onClick={goBack}
            variant="outline"
          >
            Go Back To Groups <Undo2 />
          </Button>
        </div>
      </div>
      {membersToRemove && group && (
        <RemoveGroupMembersModal
          groupName={group.name}
          isOpen={!!membersToRemove}
          onOpenChange={() => setMembersToRemove(null)}
          onSubmit={removeGroupMembers}
          participantsStr={membersToRemove.participantsStr}
        />
      )}
      {group && (
        <AddParticipantsModal
          groupName={group.name}
          isOpen={showAddParticipantsModal}
          onOpenChange={setShowAddParticipantsModal}
          submitAddTeams={addTeams}
          submitAddUsers={addUsers}
        />
      )}
    </>
  );
});
